<template>
	<div style="position: relative">
		<div style="display: flex; flex-direction: column; gap: 10px">
			<div style="display: flex; align-items: center; gap: 12px">
				<div>
					<button
						class="panel-close-button"
						@click="$emit('closePanel')"
						:title="$t('features.featurePanel.minimize')"
					>
						<svg-icon type="mdi" :path="mdiChevronRight"></svg-icon>
					</button>
				</div>
				<div>
					<h4>{{ $t("common.panels.features") }}</h4>
				</div>
			</div>

			<div>
				<FeatureTree :nodes="featureNodes" :indent="false" />
			</div>
		</div>
	</div>
</template>

<script setup lang="ts">
import { computed } from "vue";
import { useI18n } from "vue-i18n";
import { useMainStore } from "@/store/mainStore";
import SvgIcon from "@jamescoyle/vue-icon";
import { mdiChevronRight } from "@mdi/js";
import { FeatureType } from "@/model/FeatureType";
import FeatureTree from "./FeatureTree.vue";
import { TreeNode } from "./FeatureTree.vue";
import { IFeature } from "@/model/IFeature";

interface IGroup {
	name: string;
	members: IFeature[];
}

const emit = defineEmits(["closePanel"]);

const store = useMainStore();
const { t } = useI18n();

const weldlines = store.weldlines;
const cleanWeldlines = computed((): boolean => {
	return store.weldlines.some((w) => w.clean);
});
// const weldedCorner = store.weldlines.flatMap(l => l.weldCorners).filter(c => c.weldCorner)
const featureNodes = computed((): TreeNode[] => {
	return getFeatureTree();
});

const getFeatureTree = () => {
	const tree: TreeNode[] = [];
	addNode(tree, FeatureType.ScrewThread, t("features.featurePanel.groups.screwThread"), getScrewThreadNodeName);
	addNode(tree, FeatureType.Countersink, t("features.featurePanel.groups.countersink"), getCountersinkNodeName);
	addNode(tree, FeatureType.ExtrudedThread, t("features.featurePanel.groups.extrudedThread"), getExtrudedThreadNodeName);
	addNode(tree, FeatureType.CylinderHeadBoltHole, t("features.featurePanel.groups.cylinderHeadBoltHole"), getCylinderheadNodeName);
	addNode(tree, FeatureType.PressJoin, t("features.featurePanel.groups.pressJoin"), getPressJoinNodeName);
	addNode(tree, FeatureType.Engraving, t("features.featurePanel.groups.engraving"), (feature) => t("features.featurePanel.labels.engraving", { id: feature.featureId }));
	addNode(
		tree,
		FeatureType.Hole,
		t("features.featurePanel.groups.hole"),
		(feature) => t("features.featurePanel.labels.diameter", { value: Math.round(feature.radius * 20) / 10 })
	);
	addNode(
		tree,
		FeatureType.Cutout,
		t("features.featurePanel.groups.cutout"),
		(feature) => t("features.featurePanel.labels.cutout", { id: feature.featureId })
	);
	addNode(
		tree,
		FeatureType.Weldline,
		`${t("features.featurePanel.groups.weldline")} (${cleanWeldlines ? t("features.featurePanel.weldlineStatus.cleaned") : t("features.featurePanel.weldlineStatus.uncleaned")})`,
		(feature) => t("features.featurePanel.labels.weldline", { id: feature.featureId })
	);
	if (store.article.hasElevatedRights) {
		addNode(
			tree,
			FeatureType.AnchorFace,
			t("features.featurePanel.groups.anchorFace"),
			(feature) => t("features.featurePanel.labels.anchorFace", { id: feature.featureId })
		);
		addNode(
			tree,
			FeatureType.BottomFace,
			t("features.featurePanel.groups.bottomFace"),
			(feature) => t("features.featurePanel.labels.bottomFace", { id: feature.featureId })
		);
		addNode(
			tree,
			FeatureType.SideFace,
			t("features.featurePanel.groups.sideFace"),
			(feature) => t("features.featurePanel.labels.sideFace", { id: feature.featureId })
		);
	}
	return tree;
};

const addNode = (
	tree: TreeNode[],
	featureType: FeatureType,
	title: string,
	getNodeName: (feature: IFeature) => string
) => {
	if (featureType === FeatureType.Weldline && weldlines.length > 0) {
		const node: TreeNode = { label: title, key: "Group " + FeatureType[featureType] };
		tree.push(node);
		node.children = [];
		for (const weldline of weldlines) {
			let title = t("features.featurePanel.labels.weldlineTitle", {
				id: weldline.weldlineId,
				length: Math.round(weldline.length * 10) / 10,
			});
			const weldedCornersCount = weldline.weldCorners
				? weldline.weldCorners.filter((c: any) => c.weldCorner).length
				: 0;
			if (weldedCornersCount > 0) {
				title =
					title +
					(weldedCornersCount > 1
						? t("features.featurePanel.labels.weldlineCornerPlural")
						: t("features.featurePanel.labels.weldlineCornerSingular"));
			}
			node.children.push({
				label: title,
				key: "weldline " + weldline.weldlineId,
				featureId: weldline.weldlineId!,
			});
		}
		return;
	}
	const featuresOfType = store.features.filter((f: IFeature) => f.featureType === featureType);
	if (featuresOfType.length > 0) {
		const node: TreeNode = { label: title, key: "Group " + FeatureType[featureType] };
		tree.push(node);
		node.children = [];
		const groups = groupFeatures(featuresOfType, getNodeName);
		for (const group of groups) {
			if (group.members.length === 1) {
				node.children.push({
					label: getNodeName(group.members[0]),
					key: group.members[0].featureId.toString(),
					featureId: group.members[0].featureId,
				});
			} else {
				const groupNode: TreeNode = {
					label: `${group.members.length} x ${group.name}`,
					key: "Group " + group.name,
				};
				groupNode.children = [];
				node.children.push(groupNode);
				for (const feature of group.members) {
					groupNode.children.push({
						label: group.name,
						key: feature.featureId.toString(),
						featureId: feature.featureId,
					});
				}
			}
		}
	}
};

const groupFeatures = (features: any, getNodeName: (feature: IFeature) => string) => {
	const groups: IGroup[] = [];
	for (const feature of features) {
		const name = getNodeName(feature);
		let group = groups.find((g) => g.name === name);
		if (group === undefined) {
			group = {
				name: name,
				members: [],
			};
			groups.push(group);
		}
		group.members.push(feature);
	}
	return groups;
};

const getScrewThreadNodeName = (feature: IFeature) => {
	const tool = store.screwThreadParameters.find((p) => p.toolId === feature.toolId);
	if (tool) {
		return `${tool!.bezeichnung}`;
	}
	return t("features.featurePanel.undetermined");
};
const getExtrudedThreadNodeName = (feature: IFeature) => {
	const tool = store.extrudedThreadParameters.find((p) => p.toolId === feature.toolId);
	if (tool) {
		return `${tool.bezeichnung}`;
	}
	return t("features.featurePanel.undetermined");
};
const getCountersinkNodeName = (feature: IFeature) => {
	const tool = store.countersinkParameters.find((p) => p.toolId === feature.toolId);
	if (tool !== undefined) {
		return `${tool.bezeichnung}`;
	} else {
		return `${feature.radius * 2} / ${feature.innerRadius * 2}mm`;
	}
};
const getCylinderheadNodeName = (feature: IFeature) => {
	const tool = store.cylinderheadBoltHoleParameters.find((p) => p.toolId === feature.toolId);
	if (tool !== undefined) {
		return `${tool.bezeichnung}`;
	} else {
		return `${feature.radius * 2} / ${feature.innerRadius * 2}mm`;
	}
};
const getPressJoinNodeName = (feature: IFeature) => {
	const tool = store.pressJoinParameter.find((p) => p.toolId === feature.toolId);
	if (tool !== undefined) {
		return `${tool.bezeichnung}`;
	} else {
		return `${feature.radius * 2} / ${feature.innerRadius * 2}mm`;
	}
};
</script>
